/**
 * 图表编辑器「切换图表」大型运行时配置表。
 *
 * ## 本文件放什么（chartSwitch/config.ts）
 *
 * - `ChartTypeInfos`：每种 chart_type 的槽位数量规则、禁用文案、分组（~600 行能力矩阵）
 * - `SwitchChartTypes`：拖拉拽页切换下拉的可选类型及顺序（与 ChartTypeInfos 编排一体）
 *
 * ## 与 types.ts / constants.ts 的分工
 *
 * - types.ts：单行结构契约（`IChartTypeInfo`）+ 槽位标识（`CalculationType`）
 * - constants.ts：轻量映射（分组标题、切图条 icon、CHART_SWITCH_*）
 * - config.ts（本文件）：大型配置表，依赖 types 中的接口做 `satisfies Record<…>`
 */

import { ChartType, type TChartType } from '@dbl/core/chart';
import type { IChartTypeInfo } from './types';

/**
 * 拖拉拽页：右上角「切换图表」下拉可选的 chart_type 列表（顺序即 UI 展示顺序）。
 *
 * @core 多维 Analysis model 保存 chart_type 与 SwitchChartType 组件共用此顺序。
 */
export const SwitchChartTypes = [
  ChartType.Table,
  ChartType.Pie,
  ChartType.Bar,
  ChartType.HorizontalBarChart,
  ChartType.StackBar,
  ChartType.PercentStackBar,
  ChartType.Line,
  ChartType.PercentLine,
  ChartType.MixedChart,
  ChartType.BigNumber,
  ChartType.Trend,
  ChartType.DimNumberTrend,
  ChartType.Map,
  ChartType.WordCloud,
  ChartType.EventLine,
] as const satisfies readonly TChartType[];

export type TSwitchChartType = (typeof SwitchChartTypes)[number];

/**
 * 拖拉拽页切换图表：各 chart_type 的字段数量规则、禁用提示与分组归属。
 *
 * @core SortableDrag 各槽位、Search 校验、BatchAddField 禁用、Dashboard 切图卡片
 *       均读同一能力矩阵；放 metricSortable 或 DashboardLayout 会导致双份规则漂移。
 */
export const ChartTypeInfos = {
  [ChartType.Table]: {
    group: 'table',
    nameCn: '文本表格',
    nameEn: 'Text Table',
    icon: 'chart-table',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多指标',
        en: '1 or more metrics',
      },
    },
    dimension: {
      operator: '>=',
      count: 0,
      info: {
        cn: '1个或更多维度',
        en: '1 or more dimensions',
      },
      warning: {
        cn: '分类维度为空，可能影响计算性能',
        en: 'DIMENSIONS is empty, which may affect the calculation performance',
      },
    },
    rowColumn: {
      operator: '>',
      count: 0,
      info: {
        cn: '1个或更多列',
        en: '1 or more columns',
      },
    },
  },
  [ChartType.Pie]: {
    group: 'pie',
    nameCn: '饼状图',
    nameEn: 'Pie Chart',
    icon: 'chart-pie',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个指标',
        en: '1 metric',
      },
      disabled: {
        cn: '该图表仅需要一个指标',
        en: 'This chart only requires one indicator',
      },
    },
    dimension: {
      operator: '>=',
      count: 0,
      info: {
        cn: '1个或更多维度',
        en: '1 or more dimensions',
      },
      warning: {
        cn: '分类维度为空，可能影响计算性能',
        en: 'DIMENSIONS is empty, which may affect the calculation performance',
      },
    },
  },
  [ChartType.Bar]: {
    group: 'bar',
    nameCn: '柱状图',
    nameEn: 'Bar Chart',
    icon: 'chart-bar',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多指标',
        en: '1 or more metrics',
      },
      // disabled: {
      //   cn: '该图表仅需要一个指标',
      //   en: 'This chart only requires one indicator'
      // },
    },
    dimension: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多维度',
        en: '1 or more dimensions',
      },
      warning: {
        cn: '分类维度为空，可能影响计算性能',
        en: 'DIMENSIONS is empty, which may affect the calculation performance',
      },
    },
  },
  [ChartType.HorizontalBarChart]: {
    group: 'bar',
    nameCn: '条形图',
    nameEn: 'Horizontal Bar',
    icon: 'chart-horizontal-bar',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多指标',
        en: '1 or more metrics',
      },
    },
    dimension: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多维度',
        en: '1 or more dimensions',
      },
    },
  },
  [ChartType.StackBar]: {
    group: 'bar',
    nameCn: '堆叠柱状图',
    nameEn: 'Stacked Bar',
    icon: 'chart-stack-bar',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个指标',
        en: '1 metric',
      },
      disabled: {
        cn: '该图表仅需要一个指标',
        en: 'This chart only requires one metric',
      },
    },
    dimension: {
      operator: '>=',
      count: 1,
      // haveDate: true, // 必须包含日期类型
      info: {
        cn: '1个或更多维度',
        en: '1 or more dimensions',
      },
    },
  },
  [ChartType.PercentStackBar]: {
    group: 'bar',
    nameCn: '百分比堆叠柱状图',
    nameEn: 'Percent Stacked Bar',
    icon: 'chart-percent-stack-bar',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个指标',
        en: '1 metric',
      },
      disabled: {
        cn: '该图表仅需要一个指标',
        en: 'This chart only requires one metric',
      },
    },
    dimension: {
      operator: '>=',
      count: 1,
      // haveDate: true, // 必须包含日期类型
      info: {
        cn: '1个或更多维度',
        en: '1 or more dimensions',
      },
    },
  },
  [ChartType.Line]: {
    group: 'line',
    nameCn: '折线图',
    nameEn: 'Line',
    icon: 'chart-line',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多指标',
        en: '1 or more metrics',
      },
      // disabled: {
      //   cn: '该图表仅需要一个指标',
      //   en: 'This chart only requires one indicator'
      // },
    },
    dimension: {
      operator: '>=',
      count: 1,
      // 超过1个维度需要日期维度限制
      // oneMoreHaveDate: true,
      info: {
        cn: '1个或更多维度',
        en: '1 or more dimensions',
      },
    },
  },
  [ChartType.PercentLine]: {
    group: 'line',
    nameCn: '百分比折线图',
    nameEn: 'Percent Line',
    icon: 'chart-percent-line',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个指标',
        en: '1 metric',
      },
      disabled: {
        cn: '该图表仅需要一个指标',
        en: 'This chart only requires one metric',
      },
    },
    dimension: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多维度',
        en: '1 or more dimensions',
      },
    },
  },
  [ChartType.EventLine]: {
    group: 'line',
    nameCn: '事件折线图',
    nameEn: 'Event Line',
    icon: 'chart-event-line',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多指标',
        en: '1 or more metrics',
      },
      // disabled: {
      //   cn: '该图表仅需要一个指标',
      //   en: 'This chart only requires one indicator'
      // },
    },
    dimension: {
      operator: '>=',
      count: 1,
      haveDate: false,
      type: 'date',
      // 超过1个维度需要日期维度限制
      oneMoreHaveDate: true,
      info: {
        cn: '1个或更多维度（多维度时，需1个日期字段）',
        en: '1 or more dimensions(Mult-dimension requires 1 date)',
      },
    },
  },
  [ChartType.BigNumber]: {
    group: 'indicator',
    nameCn: '数字卡片',
    nameEn: 'Big Number',
    icon: 'chart-number',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个指标',
        en: '1 metric',
      },
      disabled: {
        cn: '该图表仅需要一个指标',
        en: 'This chart only requires one indicator',
      },
    },
    dimension: {
      hidden: true,
      operator: '===',
      count: 0,
      info: {
        cn: '',
        en: '',
      },
    },
  },
  [ChartType.Trend]: {
    group: 'indicator',
    nameCn: '趋势图',
    nameEn: 'Trend',
    icon: 'chart-trend',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个指标',
        en: '1 metric',
      },
      disabled: {
        cn: '该图表仅需要一个指标',
        en: 'This chart only requires 1 metric',
      },
    },
    dimension: {
      hidden: true,
      operator: '===',
      count: 0,
      info: {
        cn: '',
        en: '',
      },
    },
    xAxis: {
      operator: '===',
      count: 1,
      haveDate: true, // 必须包含日期类型
      type: 'date', // 允许的字段类型,
      info: {
        cn: '1个日期字段',
        en: '1 date',
      },
      disabled: {
        cn: '该图表仅需要一个日期维度',
        en: 'This chart only requires one date dimension',
      },
    },
    // [CalculationType.X_Axis]: {
    // 	operator: '===',
    // 	count: 1,
    // 	type: 'date', // 允许的字段类型
    // 	info: {
    // 		cn: '',
    // 		en: '',
    // 	},
    // }
  },
  [ChartType.DimNumberTrend]: {
    group: 'indicator',
    nameCn: '分维度趋势卡片图',
    nameEn: 'Big Number With Trendline (By Dimension)',
    icon: 'number-trend',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个指标',
        en: '1 metric',
      },
      disabled: {
        cn: '该图表仅需要一个指标',
        en: 'This chart only requires 1 metric',
      },
    },
    dimension: {
      hidden: true,
      operator: '===',
      count: 0,
      info: {
        cn: '',
        en: '',
      },
    },
    xAxis: {
      operator: '===',
      count: 1,
      haveDate: true, // 必须包含日期类型
      type: 'date', // 允许的字段类型,
      info: {
        cn: '1个日期字段',
        en: '1 date',
      },
      disabled: {
        cn: '该图表仅需要一个日期维度',
        en: 'This chart only requires one date dimension',
      },
    },
    showDimension: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个展示维度',
        en: '1 display dimension',
      },
      disabled: {
        cn: '该图表仅需要一个展示维度',
        en: 'This chart only requires one display dimension',
      },
      mapdisabled: {
        cn: '请配置维度内容',
        en: 'Please configure dimension content',
      },
    },
  },
  [ChartType.MixedChart]: {
    group: 'bar',
    nameCn: '组合图',
    nameEn: 'Mixed Chart',
    icon: 'chart-line-bar',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '===',
      count: 2,
      info: {
        cn: '2个指标',
        en: '2 metrics',
      },
      disabled: {
        cn: '只需要2个指标',
        en: 'Only 2 metrics are needed',
      },
    },
    dimension: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个维度',
        en: '1 dimension',
      },
      disabled: {
        cn: '只需要1个维度',
        en: 'Only 1 dimension is needed',
      },
    },
  },
  [ChartType.Map]: {
    group: 'others',
    nameCn: '地图',
    nameEn: 'Map',
    icon: 'chart-map',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个指标',
        en: '1 metric',
      },
      disabled: {
        cn: '该图表仅需要1个指标',
        en: 'This chart only requires 1 metric',
      },
    },
    dimension: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个地理维度',
        en: '1 geographical dimension',
      },
      disabled: {
        cn: '该图表仅需要1个地理维度',
        en: 'This chart only requires 1 geographical dimension',
      },
    },
  },
  [ChartType.WordCloud]: {
    group: 'others',
    nameCn: '词云图',
    nameEn: 'WordCloud Chart',
    icon: 'chart-word-cloud',
    filter: {
      operator: '>=',
      count: 1,
      info: {
        cn: '1个或更多过滤条件',
        en: '1 or more filters',
      },
    },
    metric: {
      operator: '<=',
      count: 2,
      info: {
        cn: '1个或2个指标',
        en: '1 or 2 metric',
      },
      disabled: {
        cn: '该图表仅需要1个或2个指标',
        en: 'This chart only requires 1 or 2 metric',
      },
    },
    dimension: {
      operator: '===',
      count: 1,
      info: {
        cn: '1个维度',
        en: '1 dimension',
      },
      disabled: {
        cn: '只需要1个维度',
        en: 'Only 1 dimension is needed',
      },
    },
    info: {
      cn: '示例1：<br/>筛选器：日期，国家<br/>指标：声量，情感分<br/>维度：关键词<br/><br/>示例2：<br/>筛选器：日期，国家<br/>指标：声量<br/>维度：关键词',
      en: 'Example1:<br/>Filter: Date, Country<br/>Metrics: Mentions, Sentiment<br/>Dimension: Keywords<br/><br/>Example2:<br/>Filter: Date, Country<br/>Metrics: Mentions<br/>Dimension: Keywords',
    },
  },
} as const satisfies Record<TChartType, IChartTypeInfo>;